import { Navigate, Route, Routes } from "react-router-dom";

import { RequireAuth } from "@/auth/RequireAuth";
import { RoleGate } from "@/auth/RoleGate";
import { ConferenceLayout } from "@/features/conference/ConferenceLayout";
import { LandingPage } from "@/features/conference/LandingPage";
import { CfpPage } from "@/features/conference/CfpPage";
import { SchedulePage } from "@/features/conference/SchedulePage";
import { VenuePage } from "@/features/conference/VenuePage";
import { ConferenceRegisterPage } from "@/features/conference/RegisterPage";
import { PassiGuidePage } from "@/features/conference/PassiGuidePage";
import { ConferenceLoginPage } from "@/features/conference/LoginPage";
import { SetPasswordPage } from "@/features/conference/SetPasswordPage";
import { AccountSecurityPage } from "@/features/conference/AccountSecurityPage";
import { DashboardPage } from "@/features/conference/DashboardPage";
import { ReviewsPage } from "@/features/conference/ReviewsPage";
import { ReviewAssignmentPage } from "@/features/conference/ReviewAssignmentPage";
import { SubmitPaperPage } from "@/features/conference/SubmitPaperPage";
import { PapersPage } from "@/features/conference/PapersPage";
import { PaperReaderPage } from "@/features/conference/PaperReaderPage";
import { VerifyPage } from "@/features/conference/VerifyPage";
import { AdminHomePage } from "@/features/conference/admin/AdminHomePage";
import { AdminAssignPapersPage } from "@/features/conference/admin/AdminAssignPapersPage";
import { AdminViewReviewPage } from "@/features/conference/admin/AdminViewReviewPage";
import { AdminHotelReservationPage } from "@/features/conference/admin/AdminHotelReservationPage";
import { AdminRegistrationsPage } from "@/features/conference/admin/AdminRegistrationsPage";
import { AdminPapersPage } from "@/features/conference/admin/AdminPapersPage";
import { AdminPublishPaperPage } from "@/features/conference/admin/AdminPublishPaperPage";
import { AdminRemindersPage } from "@/features/conference/admin/AdminRemindersPage";
import { AdminReviewDecisionPage } from "@/features/conference/admin/AdminReviewDecisionPage";
import { AdminReviewersPage } from "@/features/conference/admin/AdminReviewersPage";
import { AdminSessionsPage } from "@/features/conference/admin/AdminSessionsPage";
import { AdminSettingsPage } from "@/features/conference/admin/AdminSettingsPage";
import { AdminCorrespondencePage } from "@/features/conference/admin/AdminCorrespondencePage";
import { AdminCorrespondenceHistoryPage } from "@/features/conference/admin/AdminCorrespondenceHistoryPage";
import { AdminMessagesPage } from "@/features/conference/admin/AdminMessagesPage";
import { AdminCertificatesPage } from "@/features/conference/admin/AdminCertificatesPage";
import { AdminCertificatesHistoryPage } from "@/features/conference/admin/AdminCertificatesHistoryPage";
import { AdminPastEventsPage } from "@/features/conference/admin/AdminPastEventsPage";
import { PastEventsPage } from "@/features/conference/PastEventsPage";
import { PastEventReaderPage } from "@/features/conference/PastEventReaderPage";
import { NotFoundPage } from "@/features/system/NotFoundPage";

/** Conference-only router — Apex journal routes intentionally omitted. */
export function AppRouter() {
  return (
    <Routes>
      <Route element={<ConferenceLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/call-for-papers" element={<CfpPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/venue" element={<VenuePage />} />
        <Route path="/papers" element={<PapersPage />} />
        <Route path="/papers/:paperId/read" element={<PaperReaderPage />} />
        <Route path="/past-events" element={<PastEventsPage />} />
        <Route path="/past-events/:eventId" element={<PastEventReaderPage />} />
        <Route path="/submitted-papers" element={<Navigate to="/papers" replace />} />
        <Route path="/register" element={<ConferenceRegisterPage />} />
        <Route path="/passi-guide" element={<PassiGuidePage />} />
        <Route path="/login" element={<ConferenceLoginPage />} />
        <Route path="/verify-registration/:code" element={<VerifyPage />} />
        <Route
          path="/set-password"
          element={
            <RequireAuth>
              <SetPasswordPage />
            </RequireAuth>
          }
        />
        <Route
          path="/account/security"
          element={
            <RequireAuth>
              <AccountSecurityPage />
            </RequireAuth>
          }
        />

        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reviews"
          element={
            <RequireAuth>
              <ReviewsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reviews/:assignmentId"
          element={
            <RequireAuth>
              <ReviewAssignmentPage />
            </RequireAuth>
          }
        />
        <Route
          path="/submit-paper"
          element={
            <RequireAuth>
              <SubmitPaperPage />
            </RequireAuth>
          }
        />

        <Route
          path="/admin"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminHomePage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/registrations"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminRegistrationsPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/reviewers"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminReviewersPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/reviewers/:reviewerId/assign-papers"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminAssignPapersPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/review-assignments/:assignmentId/review"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminViewReviewPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/registrations/:registrationId/hotel-reservation"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminHotelReservationPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/papers"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminPapersPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/past-events"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminPastEventsPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/papers/:paperId/publish"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminPublishPaperPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/papers/:paperId/review-decision"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminReviewDecisionPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/sessions"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminSessionsPage />
              </RoleGate>
            </RequireAuth>
          }
        />

        <Route
          path="/admin/correspondence"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminCorrespondencePage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/correspondence/history"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminCorrespondenceHistoryPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/messages"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminMessagesPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/certificates"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminCertificatesPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/certificates/history"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminCertificatesHistoryPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/reminders"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminRemindersPage />
              </RoleGate>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <RequireAuth>
              <RoleGate requireGlobalRoles={["admin"]}>
                <AdminSettingsPage />
              </RoleGate>
            </RequireAuth>
          }
        />

        <Route path="/public" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
